/**
 * Knowledge graph chat reactive variable stores
 *
 * Provides simple Apollo Client reactive variables for managing knowledge graph
 * chat state. Follows the established project pattern of using makeVar instead
 * of complex Zustand stores with methods.
 * @remarks
 * This replaces the 706-line Zustand chatStore.ts and the globalKnowledgeGraphFAB.ts
 * Zustand stores with simple reactive variables. All business logic is moved to
 * hooks and components, keeping stores as pure state containers following
 * functional programming principles.
 *
 * State includes:
 * - Messages: Chat message history
 * - Session management: Session ID, user ID, organization ID
 * - Thread management: Selected thread, thread history, search
 * - Status streaming: Agent progress, status updates, progress messages
 * - UI state: Loading, errors, display configuration
 * - Player context: Optional player ID for player-specific chats
 * - Connection state: Reset flags, connection status
 * - FAB state: Visibility, modal visibility
 * - Background handling: Track backgrounding during queries
 *
 * Consumed by hooks via useReactiveVar() from @apollo/client
 */

import { makeVar } from "@apollo/client";
import type { IMessage } from "react-native-gifted-chat";

import type { AuthUserFragment, UserThread } from "@/generated/graphql";

/**
 * Status update from SSE stream
 */
export interface IStatusUpdate {
  agent: string;
  message: string;
  stage: "start" | "progress" | "complete" | "error";
  timestamp: number;
  data?: unknown;
}

/**
 * Agent progress tracking
 */
export interface IAgentProgress {
  agent: string;
  status: "pending" | "running" | "complete" | "error";
  message: string;
  timestamp: number;
  duration?: number;
}

/**
 * Status display configuration
 */
export interface IStatusDisplayConfig {
  showProgress: boolean;
  showTimestamps: boolean;
  showDurations: boolean;
  compactMode: boolean;
}

/**
 * Connection status for SSE chat
 */
export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error";

/**
 * Chat messages array
 */
export const messagesVar = makeVar<IMessage[]>([]);

/**
 * Loading state for chat operations
 */
export const isLoadingVar = makeVar<boolean>(false);

/**
 * Error message (null when no error)
 */
export const errorVar = makeVar<string | null>(null);

/**
 * Current session ID (format: organizationId#userId#uuid)
 */
export const sessionIdVar = makeVar<string>("");

/**
 * Current user ID
 */
export const userIdVar = makeVar<string>("");

/**
 * Current organization ID
 */
export const organizationIdVar = makeVar<string>("");

/**
 * Current authenticated user
 */
export const currentUserVar = makeVar<AuthUserFragment | null>(null);

/**
 * Optional player ID for player-specific chat context
 */
export const playerIdVar = makeVar<string | undefined>(undefined);

/**
 * Currently selected thread ID (null when no thread selected)
 */
export const selectedThreadIdVar = makeVar<string | null>(null);

/**
 * Thread history array
 */
export const threadHistoryVar = makeVar<UserThread[]>([]);

/**
 * Thread search query string
 */
export const threadSearchQueryVar = makeVar<string>("");

/**
 * Connection status for SSE chat
 */
export const connectionStatusVar = makeVar<ConnectionStatus>("disconnected");

/**
 * Current status update from SSE stream
 */
export const currentStatusVar = makeVar<IStatusUpdate | null>(null);

/**
 * Agent progress tracking array
 */
export const agentProgressVar = makeVar<IAgentProgress[]>([]);

/**
 * Whether to show status updates in UI
 */
export const showStatusUpdatesVar = makeVar<boolean>(true);

/**
 * Status display configuration
 */
export const statusDisplayConfigVar = makeVar<IStatusDisplayConfig>({
  showProgress: true,
  showTimestamps: false,
  showDurations: true,
  compactMode: false,
});

/**
 * Minimal shape needed to abort the answer stream currently in flight.
 */
export interface AbortableStream {
  close: () => void;
}

/**
 * The answer stream currently in flight, or null when idle.
 *
 * SE-6900: replaces `shouldResetConnectionVar`, a boolean written by both
 * `createNewThread` and `useConnectionOperations` and read by nothing — so the
 * connection teardown it was named for never happened. Holding the stream
 * handle itself lets any caller actually abort it. Access it through
 * `features/knowledge-graph/utils/activeStream`, never directly.
 */
export const activeStreamVar = makeVar<AbortableStream | null>(null);

/**
 * Current progress message ID (null when no progress message exists)
 * Used to track and update the in-flight progress message during streaming
 */
export const currentProgressMessageIdVar = makeVar<string | null>(null);

// SE-7443 removed `backgroundedDuringQueryVar`. It was declared here as a flag
// for "stuck loading states when app returns from background" and never written
// by anything — a third dead reactive var of exactly the kind SE-6900 deleted
// `shouldResetConnectionVar` for. Backgrounding is now handled where the state
// actually lives, in `useSSE`'s foreground clock, with no global to keep in
// sync. Do not reintroduce it: a flag nothing writes reads as coverage that
// does not exist.

/**
 * FAB visibility state
 * Controls whether the floating action button is shown
 */
export const fabVisibleVar = makeVar<boolean>(true);

/**
 * FAB modal visibility state
 * Controls whether the knowledge graph chat modal is open
 */
export const fabModalVisibleVar = makeVar<boolean>(false);

/**
 * Number of reconnection attempts
 * Tracks how many times the SSE connection has attempted to reconnect
 */
export const reconnectAttemptsVar = makeVar<number>(0);
